import { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Dialog,
  Button,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText,
} from '@mui/material';
import { useSelector } from '../../../../redux/store';
import { checkVendorOrderSent } from '../../../../api/orders';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

export default function ReRunDropdown() {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { orderById } = useSelector((state) => state.orders || {});
  const { items = [] } = orderById[id] || {};

  const [dialogOpen, setDialogOpen] = useState(false);
  const [vendId, setVendId] = useState(null);
  const [vendorsSent, setVendorsSent] = useState([]);

  const handleClose = () => {
    setDialogOpen(false);
  };

  const vendorIdFromUrl = searchParams.get('vendorId');
  const isVendorView = !!vendorIdFromUrl && vendorIdFromUrl !== 'all';

  const vendors = useMemo(() => {
    if (!items) return [];
    const vendorIds = Array.from(new Set(items.map((item) => item.vendorId)));
    const uniqueVendors = vendorIds.map((vendorId) => {
      const vendorItems = items.filter((item) => item.vendorId === vendorId);
      return {
        vendor: vendorItems[0].vendor,
        vendorId,
        disabled: vendorItems.every((item) => item.deleted === true),
      };
    });
    return isVendorView ? uniqueVendors.filter((v) => String(v.vendorId) === String(vendorIdFromUrl)) : uniqueVendors;
  }, [items, isVendorView, vendorIdFromUrl]);

  const navigateToRerun = (vendorId) => {
    const params = new URLSearchParams(searchParams);
    params.set('filter', 'rerun');
    params.set('vendorId', String(vendorId));
    params.set('rerunFromVendorView', isVendorView ? '1' : '0');
    navigate(`/dashboard/orders/${id}?${params.toString()}`);
  };

  const resendAllDisabled = vendors.every((vendor) => vendor.disabled);

  const checkVendorOrder = async (vendorId) => {
    const response = await checkVendorOrderSent(id, [vendorId]);
    if (response.status === 200) {
      if (response.data.length > 0) {
        setVendorsSent(response.data);
        setVendId(vendorId);
        setDialogOpen(true);
      } else {
        navigateToRerun(vendorId);
      }
    }
  };

  const checkAllVendorOrders = async (vendorIds) => {
    const response = await checkVendorOrderSent(id, vendorIds);
    if (response.status === 200) {
      if (response.data.length > 0) {
        setVendorsSent(response.data);
        setVendId(0);
        setDialogOpen(true);
      } else {
        navigateToRerun('all');
      }
    }
  };

  const rowSx = {
    fontSize: '0.875rem',
    color: 'grey.700',
    cursor: 'pointer',
    borderRadius: 1.5,
    px: 1,
    mx: -1,
    py: 0.5,
    '&:hover': {
      bgcolor: 'grey.100',
      color: 'text.primary',
    },
  };

  const disabledRowSx = {
    ...rowSx,
    color: 'text.disabled',
    cursor: 'default',
    '&:hover': {},
  };

  return (
    <>
      <Stack spacing={0.5} sx={{ pl: 5 }}>
        {vendors.length > 1 && !isVendorView && (
          <Box
            onClick={() =>
              !resendAllDisabled &&
              checkAllVendorOrders(vendors.filter((vendor) => !vendor.disabled).map((vendor) => vendor.vendorId))
            }
            sx={resendAllDisabled ? disabledRowSx : rowSx}
          >
            All Vendors
          </Box>
        )}
        {vendors.map((vendor) => (
          <Box
            key={vendor.vendorId}
            onClick={() => !vendor.disabled && checkVendorOrder(vendor.vendorId)}
            sx={vendor.disabled ? disabledRowSx : rowSx}
          >
            {vendor.vendor}
          </Box>
        ))}
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ textAlign: 'center' }}>
          Confirm
          <CloseIconButton onClick={handleClose} />
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {vendorsSent.map((vendor) => vendor).join(', ')} order{vendorsSent.length > 1 && 's'} has been marked as
            sent. Are you sure you want to rerun?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            sx={{ width: 150 }}
            onClick={() => {
              navigateToRerun(vendId);
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
